/**
 * Guardian community-report confirmation card (2026-09-24, agent-voice-auth).
 *
 * Rendered inline in the chat thread when the agent proposes an
 * `incident_create_or_update` report. Nothing is submitted until the person
 * taps Confirm — typed text never approves it (see useChatApp.js and
 * utils/confirmationCards.js for the state machine this reflects).
 *
 * Card kind 'incident_report' (incident_create_or_update, schema_version 1).
 * Moved from components/chat/ConfirmationCard.js into the per-tool card
 * registry unchanged (plan 05 B3, 2026-09-29; see ./index.js).
 */
import React from 'react';
import { Box, Paper, Typography, Button } from '@mui/material';

/**
 * Values are shown exactly as the server will submit them — no rounding or
 * relabeling — so what the person confirms is what gets filed.
 */
function formatValue(value) {
  return value === null || value === undefined || value === '' ? '—' : String(value);
}

/**
 * @param {object} props
 * @param {object} props.message - A `{type: 'confirmation', ...}` chat message.
 * @param {boolean} props.isLoading - True while another agent turn is in flight.
 * @param {(confirmationId: string) => void} props.onConfirm
 */
export default function IncidentReportCard({ message, isLoading, onConfirm }) {
  const proposed = message.proposed || {};
  const { state } = message;
  const isStale = state === 'stale' || state === 'resolved' || state === 'unknown';
  const isSubmitting = state === 'submitting';
  const canConfirm = state === 'pending' && !isLoading;

  return (
    <Paper
      className="il-assistant-bubble"
      variant="outlined"
      sx={{
        p: 2,
        maxWidth: '70%',
        borderRadius: '18px 18px 18px 4px',
        borderColor: state === 'pending' || state === 'submitting' ? 'primary.main' : 'divider',
        opacity: isStale ? 0.7 : 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Confirm community report
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
        <Typography variant="body2">
          <strong>Title:</strong> {proposed.title || '—'}
        </Typography>
        <Typography variant="body2">
          <strong>Category:</strong> {formatValue(proposed.category)}
        </Typography>
        {proposed.location && (
          <Typography variant="body2">
            <strong>Location:</strong> {proposed.location}
          </Typography>
        )}
        <Typography variant="body2">
          <strong>Latitude:</strong> {formatValue(proposed.latitude)}
          {'   '}
          <strong>Longitude:</strong> {formatValue(proposed.longitude)}
        </Typography>
        {proposed.certainty_radius_meters != null && (
          <Typography variant="body2">
            <strong>Certainty radius:</strong> {proposed.certainty_radius_meters} m
          </Typography>
        )}
        {proposed.description && (
          <Typography variant="body2">
            <strong>Description:</strong> {proposed.description}
          </Typography>
        )}
      </Box>

      {state === 'unknown' ? (
        <Typography variant="body2" color="warning.main">
          Outcome unknown — the connection dropped while submitting. Check before reporting again.
        </Typography>
      ) : state === 'resolved' ? (
        <Typography variant="body2" color="text.secondary">
          Answered — see the reply below.
        </Typography>
      ) : state === 'stale' ? (
        <Typography variant="body2" color="text.secondary">
          No longer valid — ask again.
        </Typography>
      ) : (
        <>
          <Button
            variant="contained"
            size="small"
            disabled={!canConfirm}
            onClick={() => onConfirm(message.confirmationId)}
          >
            {isSubmitting ? 'Confirming…' : 'Confirm report'}
          </Button>
          {state === 'pending' && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Nothing is submitted until you confirm.
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}
